import { useLayoutEffect, useRef, useState } from "react";

import { useResizeObserver } from "./useResizeObserver.ts";

export const useMeasureOnResize = <T>(
  elementRef: React.RefObject<HTMLElement | null>,
  measure: (element: HTMLElement, shouldCancel: () => boolean) => T,
  deps: React.DependencyList,
  initialValue: T,
) => {
  const [value, setValue] = useState(initialValue);
  const box = useResizeObserver(elementRef);
  const runIdRef = useRef(0);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const currentRun = runIdRef.current + 1;
    runIdRef.current = currentRun;

    const frameId = requestAnimationFrame(() => {
      if (runIdRef.current !== currentRun || !element) return;

      const next = measure(element, () => runIdRef.current !== currentRun);
      if (runIdRef.current !== currentRun) return;

      setValue(next);
    });

    return () => {
      runIdRef.current += 1;
      cancelAnimationFrame(frameId);
    };
    // The spread of `...deps` in the dependency array is intentional: callers pass additional deps
    // that cannot be statically analyzed. Both react-compiler and exhaustive-deps rules are suppressed
    // because they do not support spread elements in dependency arrays.
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box, elementRef, measure, ...deps]);

  return value;
};
